import { useCallback } from "react";

import { useBarrelRoll } from "./use-barrel-roll";
import { useConfetti } from "./use-confetti";

const EASTER_EGGS = {
  "do a barrel roll": "barrelRoll",
  "!party": "confetti",
} as const;

/**
 * Whether a search term is a hidden easter-egg phrase. Pure and stateless, so
 * callers (e.g. the debounced search effect) can cheaply skip firing a real API
 * query for these phrases.
 */
export const isEasterEggPhrase = (term: string): boolean =>
  term.trim().toLowerCase() in EASTER_EGGS;

/**
 * Hook that maps hidden search phrases to playful screen effects, keeping the
 * easter-egg logic out of the search component's business logic.
 *
 * Returns `executeEasterEgg`, which runs the effect for a matching phrase.
 * Callers should gate it behind `isEasterEggPhrase` (e.g. on Enter); unknown
 * terms are a no-op.
 */
export const useEasterEgg = () => {
  const { rollBarrel } = useBarrelRoll();
  const { throwConfetti } = useConfetti();

  const executeEasterEgg = useCallback(
    (term: string): void => {
      const normalized = term.trim().toLowerCase();
      const effect = EASTER_EGGS[normalized as keyof typeof EASTER_EGGS];

      switch (effect) {
        case "barrelRoll":
          rollBarrel();
          break;
        case "confetti":
          throwConfetti();
          break;
      }
    },
    [rollBarrel, throwConfetti]
  );

  return { executeEasterEgg };
};
